// @ts-ignore
global.FALLBACK_LOCALE = 'fr'

import { get as _get, set as _set } from 'lodash'
import { EMPTY, throwError } from "rxjs"
import { catchError, last } from 'rxjs/operators'

import { getTranslatedString, Formatter, FormatterSchema, PrintableReport, PrintableReportGridItem } from "@skinsoft/types"
import { fetch, Proxy } from '@skinsoft/network'
import { FormatterDataStore, EntityFieldDataStore, ContainerDataStore } from "@skinsoft/entity"

const PROXY = new Proxy()

export enum ZPLTypes {
    TEXT,
    TITLE,
    QR_CODE,
    BAR_CODE_128,
    FORMATTER,
    UNKNOWN,
}

enum Orientation {
    PORTRAIT,
    LANDSCAPE
}

export interface DrawResult {
    result: string,
    height: number,
    width?: number,
    type: ZPLTypes,
    itemX?: number,
    itemY?: number,
}

interface Font {
    height: number,
    width: number,
}
export const ZEBRA_FONTS = {
    0: {
        height: 15, // size in points
        width: 12,
    }
}

interface SpacingOptimizationOptions {
    optimizeSpacing: boolean,
    optimizeFontSize: boolean,
    optimizeCodes: boolean,
}
export interface SpacingOptimizationResult {
    data: DrawResult[][],
    spacing: number, // unit in dots
    margin: number, // unit in dots
}

// dimension in points
const ZPL_DIMENSION = {
    WIDTH_57x32_203dpi: 456,
    HEIGHT_57x32_203dpi: 256,
    WIDTH_57x32_300dpi: 684,
    HEIGHT_57x32_300dpi: 384,
}

export class ZPLGenerator {
    private locale: string
    private orientation: Orientation
    private labelWidth: number
    private labelHeight: number
    private dpmm: 8 | 12
    private font: Font
    private fontMultiplier: number
    private marginMultiplierY: number
    private marginMultiplierX: number
    
    private get textSize() {
        return Math.trunc(this.fontMultiplier * this.font.height)
    }

    // label height without margin
    private get printableZoneHeight() {
        return Math.trunc(this.labelHeight - (2 * this.marginY))
    }

    // label width without margin
    private get printableZoneWidth() {
        return Math.trunc(this.labelWidth - (2 * this.marginX))
    }

    private get marginX() {
        return Math.trunc(this.marginMultiplierX * this.textSize)
    }

    private get marginY() {
        return Math.trunc(this.marginMultiplierY * this.textSize)
    }

    public constructor(locale: string, labelHeight: number, labelWidth: number, dpmm: 8 | 12, font: Font = ZEBRA_FONTS[0], 
        fontMultiplier: number = 2.5, marginMultiplierY: number = 1.25, marginMultiplierX: number = 1.25) {

        this.locale = locale
        this.labelHeight = labelHeight
        this.labelWidth = labelWidth
        this.dpmm = dpmm
        this.font = font
        this.fontMultiplier = fontMultiplier
        this.marginMultiplierY = marginMultiplierY
        this.marginMultiplierX = marginMultiplierX
    }

    public async printableReportToZPL(printableReport: PrintableReport, entity: any): Promise<string> {
        // we load the container from API because we get a feature style container instead of a container
        // this is a temporal fix, the correct way is to handle it in the BarCodeEditor component
        // (we do it only for container, not for item)
        if(entity.itemType?.toLowerCase() === "physicalcontaineritem") {
            try {
                const newEntity = await ContainerDataStore.getInstance().fetchOne(entity.itemId ?? entity.id).toPromise()
                entity = newEntity
            } catch(err) {
    
            }
        }
    
        let zpl = '^XA^CI28\n'
    
        if(printableReport && printableReport.printableReportGridList && printableReport.printableReportGridList[0]) {
            let fields = printableReport.printableReportGridList[0].printableReportGridItems
            // we sort fields by y value and x value to facilitate positionning of elements on the label
            fields.sort((item1, item2) => {
                if(item1.y < item2.y) {
                    return -1
                } 
                else if(item1.y > item2.y) {
                    return 1
                }
                else {
                    if(item1.x < item2.x) {
                        return -1
                    }
                    else if(item1.x > item2.x) {
                        return 1
                    }
                    else {
                        return 0
                    }
                }
            })
    
            // we put a ! because there is an error in the API, the isPortrait field contains the contrary of the actual orientation
            this.orientation = !printableReport.printableReportGridList[0].isPortrait ? Orientation.PORTRAIT : Orientation.LANDSCAPE
            if(this.orientation === Orientation.PORTRAIT) {
                const labelHeight = this.labelHeight
                this.labelHeight = this.labelWidth
                this.labelWidth = labelHeight
            }
    
            zpl+=`^LL${this.labelWidth}\n` // define width of label
    
            if(fields) {
                // Building zpl for each field of printable report => we get the zpl code + the dimensions for each field but not the position on the label
                let data: DrawResult[][] = [] // [line][column]
                let previousFieldY: number | undefined
                for(const field of fields) {
                    let drawResult: DrawResult = { result: '', height: 0, type: ZPLTypes.UNKNOWN }
                    switch(field.type) {
                        case 'qrCode': 
                            drawResult = await this.drawCode(field, entity)
                            break
                        case 'text':
                            drawResult = this.drawTextField(field, this.printableZoneWidth)
                            break
                        case 'formatter':
                            let drawResults: DrawResult[] = []
                            drawResults = await this.drawFormatter(field, entity, this.printableZoneWidth)
                            drawResults = drawResults.filter(res => res.height > 0)
                            if(drawResults && drawResults.length) {
                                if(previousFieldY !== undefined && field.y === previousFieldY) {
                                    data[data.length-1] = data[data.length-1].concat(drawResults)
                                } else {
                                    data.push(drawResults)
                                }
                                drawResult.itemX = field.x
                                drawResult.itemY = field.y
                                previousFieldY = field.y
                            }
                            continue
                        case 'title':
                            drawResult = this.drawTitle(field)
                            break
                    }
                    if(drawResult.height > 0) {
                        drawResult.itemX = field.x
                        drawResult.itemY = field.y
                        if(previousFieldY !== undefined && field.y === previousFieldY) {
                            // add column to the line
                            data[data.length -1].push(drawResult)
                        } else {
                            // new line
                            data.push([drawResult])
                        }
                        previousFieldY = field.y
                    }
                }
                
                // Checking that the data fits in the label and resize if needed
                let dataTotalHeight = 0
                for(const line of data) {
                    dataTotalHeight += Math.max(...line.map(col => col.height))
                }
                let spacingOptimizationResult = this.optimizeSpacingAndFontSize(data, dataTotalHeight)
    
                // Positionning elements on the label
                zpl = this.positionDataOnLabel(zpl, spacingOptimizationResult)
            }
        }
    
        if(zpl.substring(-4) === '^FS\n') {
            zpl = zpl.substring(0, zpl.length - 4)
        }
    
        zpl += '^XZ'
    
        return zpl
    }

    /**
     * 
     * @param data Datas to print
     * @param dataTotalHeight Height of the data to print, without any spacing
     * @param options Not functional yet
     * @returns "{ spacing: the space between each line in dots, margin: the margin on top and bottom of label in dots, data: the data with updated spacing and text size }"
     */
    private optimizeSpacingAndFontSize(data: DrawResult[][], dataTotalHeight: number, options?: SpacingOptimizationOptions): SpacingOptimizationResult {

        let availableHeight = this.printableZoneHeight - dataTotalHeight
        let textIndexes: { lin: number, col: number }[] | undefined
        const NB_SPACINGS = data.length - 1

        const MAX_ITERATION = 8
        const STEP_MARGIN_MULTIPLIER = 0.25
        const STEP_FONT_MULTIPLIER = 0.25
        const MIN_MULTIPLIER_MARGIN_Y = 1
        const MIN_MULTIPLIER_FONT = 1
        const MIN_SPACING = 10
        const SPACING_MULTIPLIER = 1

        let iterations = 0
        let minSpacing = SPACING_MULTIPLIER * this.textSize
        while(availableHeight < minSpacing * NB_SPACINGS && iterations < MAX_ITERATION) { // improvement: reduce availableSpace - minSpacing * nb spacings the closest to 0 while being >= to 0 (dichotomie)
            
            // we select the param that we will decrease to increase availableHeight (we select depending on how much space is missing)
            let resizeFont: boolean
            let resizeMargin: boolean
            let stepFontMultiplier = STEP_FONT_MULTIPLIER
            if(this.marginMultiplierY <= MIN_MULTIPLIER_MARGIN_Y) {
                resizeFont = true
                resizeMargin = false
                stepFontMultiplier = STEP_FONT_MULTIPLIER / 2
            } else {
                resizeFont = availableHeight < -30 && this.fontMultiplier >= MIN_MULTIPLIER_FONT
                resizeMargin = !resizeFont
            }
            
            // adapt textSize and margins
            if(resizeFont) {
                this.fontMultiplier -= stepFontMultiplier
            }
            if(resizeMargin) {
                this.marginMultiplierY -= STEP_MARGIN_MULTIPLIER
            }

            // resize texts
            if(!textIndexes) {
                // @ts-ignore
                textIndexes = data.map((line, index) => {
                        return line.map((column, indexCol) => {
                            if(column.type === ZPLTypes.TEXT || column.type === ZPLTypes.FORMATTER) {
                                return {
                                    lin: index,
                                    col: indexCol,
                                }
                            }
                            return {}
                        })
                    })
                    .flat()
                    .filter(indexes => 'col' in indexes) // remove the {} from the indexes
            }
            for(const textIndex of textIndexes!) {
                try{
                    if(textIndex.col === 0) dataTotalHeight -= data[textIndex.lin][0].height
                    switch(data[textIndex.lin][textIndex.col].type) {
                        case ZPLTypes.TEXT:
                            data[textIndex.lin][textIndex.col] = this.resizeText(data[textIndex.lin][textIndex.col], ZEBRA_FONTS[0], this.fontMultiplier)
                            break
                        case ZPLTypes.FORMATTER:
                            data[textIndex.lin][textIndex.col] = this.resizeFormatter(data[textIndex.lin][textIndex.col], ZEBRA_FONTS[0], this.fontMultiplier)
                            break
                    }
                    if(textIndex.col === 0) dataTotalHeight += data[textIndex.lin][0].height
                } catch(error) {
                    console.log(error)
                }
            }

            // resize codes
            // not implemented yet

            availableHeight = this.printableZoneHeight - dataTotalHeight
            if(this.marginMultiplierY > MIN_MULTIPLIER_MARGIN_Y) {
                minSpacing = Math.max(SPACING_MULTIPLIER * this.textSize, MIN_SPACING)
            } else {
                minSpacing = MIN_SPACING // if we cannot reduce the margin anymore, we use the minimum spacing allowed to gain space
            }
            iterations++
        }
        return {
            data: data,
            spacing: Math.trunc(availableHeight / NB_SPACINGS),
            margin: this.marginY,
        }
    }

    /**
     * 
     * @param zpl 
     * @param spacingOptimizationResult 
     * @returns 
     */
    private positionDataOnLabel(zpl: string, spacingOptimizationResult: SpacingOptimizationResult): string {
        let x = this.marginX
        let y=spacingOptimizationResult.margin
        let spacingX = this.font.width * this.fontMultiplier * 2
        for(const line of spacingOptimizationResult.data) {
            let sanitizedLine = line.filter(col => col.height > 0)
            if(sanitizedLine.length === 1) {
                zpl += `^FO${x},${y}\n` + sanitizedLine[0].result + '^FS\n'
                y+= sanitizedLine[0].height + spacingOptimizationResult.spacing
            }
            else if(sanitizedLine.length > 1) {
                const availableXPerCol = (this.printableZoneWidth - (sanitizedLine.length - 1) * spacingX) / sanitizedLine.length
                for(const column of sanitizedLine) {
                    if(column.height <= 0) {
                        continue
                    }
                    switch(column.type) {
                        case ZPLTypes.TEXT:
                        case ZPLTypes.FORMATTER:
                            const updatedColumn = this.resizeTextBox(column, undefined, availableXPerCol)
                            column.width = updatedColumn.width
                            column.result = updatedColumn.result
                            break;
                    }
                    zpl += `^FO${x},${y}\n` + column.result + '^FS\n'
                    x += (column.width ? column.width : availableXPerCol) + spacingX
                }
                x = this.marginX
                y += Math.max(...sanitizedLine.map(col => col.height)) + spacingOptimizationResult.spacing
            }
        }

        return zpl
    }

    private drawTextField(field: PrintableReportGridItem, width: number): DrawResult {
        if(field && field.value) {
            return this.drawText(field.value, 1, width)
        }
        return { result: '', height: 0, type: ZPLTypes.TEXT }
    }

    /**
    * 
    * @param data 
    * @param nbLine 
    * @param textBoxWidth 
    * @returns 
    */
    private drawText(data: string, nbLine: number, textBoxWidth: number): DrawResult {
        if(data) {
            const textWidth = Math.trunc(this.font.width * this.fontMultiplier)
            const textBoxHeight = nbLine * this.textSize + nbLine * 5 // there is a space of ~5 points between paragraphs maybe dependent of font size
        
            return {
                type: ZPLTypes.TEXT,
                height: textBoxHeight,
                result: `^A0,${this.textSize},${textWidth}\n` +
                        `^TBN,${textBoxWidth},${textBoxHeight}\n` +
                        `^FD${data}\n`
            }
        }
    
        return { height: 0, type: ZPLTypes.TEXT, result: '' }
    }

    private drawTitle(field: PrintableReportGridItem): DrawResult {
        return { result: '', height: 0, type: ZPLTypes.TITLE }
    }

    /**
     * 
     * @param drawResult 
     * @param textBoxHeight if undefined, it will not resize
     * @param textBoxWidth if undefined, it will not resize
     * @returns 
     */
    private resizeTextBox(drawResult, textBoxHeight?: number, textBoxWidth?: number): DrawResult {
        return this.resizeTextBoxNoLookBehind(drawResult, textBoxHeight, textBoxWidth)
        /*const regexTextBoxHeight = /(?<=\^TBN,\d+,)\d+/
        const regexTextBoxWidth = /(?<=\^TBN,)\d+/

        if(textBoxHeight) {
            drawResult.result = drawResult.result.replace(regexTextBoxHeight, textBoxHeight.toString())
            drawResult.height = textBoxHeight
        }
        if(textBoxWidth) {
            drawResult.result = drawResult.result.replace(regexTextBoxWidth, textBoxWidth.toString())
            drawResult.width = textBoxWidth
        }
        return drawResult*/
    }

    private resizeTextBoxNoLookBehind(drawResult, textBoxHeight?: number, textBoxWidth?: number): DrawResult {
        const regexTextBoxLine = /(\^TBN,\d+,)\d+/
        
        const oldTextBoxLine = drawResult.result.match(regexTextBoxLine)
        const oldTextBoxLineSplitted = oldTextBoxLine && oldTextBoxLine.length ? oldTextBoxLine[0].split(',') : []
        if(oldTextBoxLineSplitted.length === 3) {
            if(textBoxHeight) {
                drawResult.height = textBoxHeight
            }
            if(textBoxWidth) {
                drawResult.width = textBoxWidth
            }

            const newTextBoxLine = oldTextBoxLineSplitted[0] + ',' + drawResult.width + ',' + drawResult.height
            drawResult.result = drawResult.result.replace(regexTextBoxLine, newTextBoxLine)
        }

        return drawResult
    }

    /**
     * 
     * @param txtDrawResult The text to resize
     * @param font 
     * @param fontMultiplier 
     * @param oldFont 
     * @param oldFontMultiplier 
     * @throws Error if not able to find old textbox height
     */
    private resizeText(txtDrawResult: DrawResult, font: Font, fontMultiplier: number, oldFont?: Font, oldFontMultiplier?: number): DrawResult {
        return this.resizeTextNoLookBehind(txtDrawResult, font, fontMultiplier, oldFont, oldFontMultiplier)
        /*const regexTextBoxHeight = /(?<=\^TBN,\d+,)\d+/
        const regexTextHeight = /(?<=\^A0,)\d+/
        const regexTextWidth = /(?<=\^A0,\d+,)\d+/
        
        let matchOldTextBoxHeight = txtDrawResult.result.match(regexTextBoxHeight)
        const oldTextBoxHeight = matchOldTextBoxHeight ? parseInt(matchOldTextBoxHeight[0]) : null

        let oldTextHeight
        if(oldFont && oldFontMultiplier) {
            oldTextHeight = oldFont.height * oldFontMultiplier
        } else {
            let matchOldTextHeight = txtDrawResult.result.match(regexTextHeight)
            oldTextHeight = matchOldTextHeight ? parseInt(matchOldTextHeight[0]) : null
        }

        if(!oldTextBoxHeight || !oldTextHeight) {
            throw new Error()
        }
        /* By looking inside drawText method, we know that
        *     oldTextBoxHeight == nbLine * oldTextHeight + (nbLine - 1) * 5
        * <=> oldTextBoxHeight == nbLine * oldTextHeight + 5nbLine - 5
        * <=> oldTextBoxHeight == (oldTextHeight + 5) * nbLine - 5
        * <=> oldTextBoxHeight - 5 == (oldTextHeight + 5) * nbLine
        * <=> (oldTextBoxHeight - 5) / (oldTextHeight + 5) == nbLine
        *//*
        const nbLine = Math.round((oldTextBoxHeight - 5) / (oldTextHeight + 5))
        
        const textHeight = Math.trunc(font.height * fontMultiplier)
        const textWidth = Math.trunc(font.width * fontMultiplier)
        let textBoxHeight = Math.trunc((textHeight * nbLine) + ((nbLine - 1) * 5))
        if(textBoxHeight < textHeight) {
            textBoxHeight = textHeight
        }
        if(textBoxHeight === textHeight) {
            textBoxHeight += 5 // in case nbLine == 1
        }

        txtDrawResult.result = txtDrawResult.result.replace(regexTextHeight, textHeight.toString())
        txtDrawResult.result = txtDrawResult.result.replace(regexTextWidth, textWidth.toString())
        txtDrawResult.result = txtDrawResult.result.replace(regexTextBoxHeight, textBoxHeight.toString())
        txtDrawResult.height = textBoxHeight

        return txtDrawResult*/
    }

    private resizeTextNoLookBehind(txtDrawResult: DrawResult, font: Font, fontMultiplier: number, oldFont?: Font, oldFontMultiplier?: number): DrawResult {
        const regexTextBoxLine = /(\^TBN,\d+,)\d+/
        const regexTextLine = /(\^A0,\d+,)\d+/

        let oldTextBoxHeight
        let oldTextHeight

        // getting old text box height
        const oldTextBoxLine = txtDrawResult.result.match(regexTextBoxLine)
        const oldTextBoxLineSplitted = oldTextBoxLine && oldTextBoxLine.length ? oldTextBoxLine[0].split(',') : []
        if(oldTextBoxLineSplitted.length === 3) {
            oldTextBoxHeight = oldTextBoxLineSplitted[2]
        }

        // getting old text height
        const oldTextLine = txtDrawResult.result.match(regexTextLine)
        const oldTextLineSplitted = oldTextLine && oldTextLine.length ? oldTextLine[0].split(',') : []
        if(oldTextLineSplitted.length === 3) {
            oldTextHeight = oldTextLineSplitted[1]
        } else {
            if(oldFont && oldFontMultiplier) {
                oldTextHeight = oldFont.height * oldFontMultiplier
            }
        }

        if(!oldTextBoxHeight || !oldTextHeight) {
            throw new Error()
        }
        /* By looking inside drawText method, we know that
        *     oldTextBoxHeight == nbLine * oldTextHeight + (nbLine - 1) * 5
        * <=> oldTextBoxHeight == nbLine * oldTextHeight + 5nbLine - 5
        * <=> oldTextBoxHeight == (oldTextHeight + 5) * nbLine - 5
        * <=> oldTextBoxHeight - 5 == (oldTextHeight + 5) * nbLine
        * <=> (oldTextBoxHeight - 5) / (oldTextHeight + 5) == nbLine
        */
        const nbLine = Math.round((oldTextBoxHeight - 5) / (oldTextHeight + 5))
        
        const textHeight = Math.trunc(font.height * fontMultiplier)
        const textWidth = Math.trunc(font.width * fontMultiplier)
        let textBoxHeight = Math.trunc((textHeight * nbLine) + ((nbLine - 1) * 5))
        if(textBoxHeight < textHeight) {
            textBoxHeight = textHeight
        }
        if(textBoxHeight === textHeight) {
            textBoxHeight += 5 // in case nbLine == 1
        }

        const newTextBoxLine = oldTextBoxLineSplitted[0] + ',' + oldTextBoxLineSplitted[1] + ',' + textBoxHeight
        const newTextLine = oldTextLineSplitted[0] + ',' + textHeight + ',' + textWidth
        txtDrawResult.result = txtDrawResult.result.replace(regexTextBoxLine, newTextBoxLine)
        txtDrawResult.result = txtDrawResult.result.replace(regexTextLine, newTextLine)

        txtDrawResult.height = textBoxHeight
        return txtDrawResult
    }

    private async drawCode(field: PrintableReportGridItem, entity: any): Promise<DrawResult> {
        if(field.qrcodeFormat && field.value) {
            const entityFieldDataStore: EntityFieldDataStore = EntityFieldDataStore.getInstance()
            const entityField = await entityFieldDataStore.fetchOne(field.value).toPromise()
    
            const path = `${entityField.path}`
            let entityValue = _get(entity, `${path}.name`) || _get(entity, path)
            
            if(entityValue) {
                if(typeof entityValue === 'object') {
                    if(Array.isArray(entityValue)) {
                        entityValue = entityValue.length ? entityValue[0] : null
                    }
                    if('value' in entityValue) {
                        entityValue = entityValue.value
                    } else if('translatedString' in entityValue) {
                        entityValue = getTranslatedString(entityValue, this.locale)
                    }
                }
            
                switch(field.qrcodeFormat) {
                    case 'CODE_128':
                        return this.drawBarCode128(entityValue, 50)
                    case 'QR_CODE':
                        return this.drawQrCode(entityValue, 4)
                    case 'AZTEC':
                        break
                    case 'DATA_MATRIX':
                        break
                    case 'CODE_39':
                        break
                }
            }
        }
        return { result: '', height: 0, type: ZPLTypes.UNKNOWN }
    }

    /**
     * 
     * @param data 
     * @param size between 1 and 10 (included) only sizes 4 to 10 are readable
     * @returns 
     */
    private drawQrCode(data, size: number = 5): DrawResult {
        if(size < 1 || size > 10) {
            return { result: '', height: 0, type: ZPLTypes.QR_CODE }
        }

        // It looks like the size in dots doesn't depends on DPI
        // key : magnification factor, value: size in dots (estimation)
        // The estimations have been made with a 302dpi printer (== 8dpmm)
        // { [size]: { [dpi]: value } }
        const QR_CODE_SIZE = {
            1: { 8: 16, 12: 24 },
            2: { 8: 32, 12: 48 },
            3: { 8: 48, 12: 72 },
            4: { 8: 108, 12: 108 },
            5: { 8: 144, 12: 144 },
            6: { 8: 104, 12: 156 },
            7: { 8: 120, 12: 180 },
            8: { 8: 136, 12: 204 },
            9: { 8: 152, 12: 228 },
            10: { 8: 168, 12: 252 },
        }

        // FDMA = FD(field data)+M(error correction level)+A(data input) 
        // See spec for ^BQ command (scroll to "Considerations for ^FD When Using the QR Code:")
        return {
            type: ZPLTypes.QR_CODE,
            height: QR_CODE_SIZE[size][this.dpmm],
            result: `^BQ,2,${size}\n`+
                    `^FDMA,${data}\n`,
        }
    }

    /**
     * 
     * @param data 
     * @param height 
     * @returns 
     */
    private drawBarCode128(data: string, height: number): DrawResult {
        let barCodeThickness = '^BY1'
        if(this.dpmm === 12) {
            barCodeThickness = '^BY2'
        }
        
        return {
            type: ZPLTypes.BAR_CODE_128,
            height: height,
            result: `${barCodeThickness}^BCN,${height},N,N,N,A\n` + 
                    `^FD${data}\n`,
        }
    }

    private async drawFormatter(field: PrintableReportGridItem, entity: any, maxWidthPerField: number, formatterId?: string): Promise<DrawResult[]> {
        let computedFormatterId
        if(formatterId) {
            computedFormatterId = formatterId
        }
        else if(field && field.value) {
            computedFormatterId = field.value
        }
    
        if(computedFormatterId) {
            let results: DrawResult[] = []
            const formatter: Formatter = await FormatterDataStore.getInstance().fetchOne(computedFormatterId).toPromise()
    
            if(formatter.formatters) {
                for(const selectedFormatter of formatter.formatters) {
                    const selectedFormatterResults = await this.drawFormatterSchema(field, selectedFormatter, entity, maxWidthPerField)
                    results = results.concat(selectedFormatterResults)
                }
            }
    
            results.filter(dr => dr.height > 0)
            return results
        }
    
        return []
    }

    private async drawFormatterSchema(field: PrintableReportGridItem, formatterSchema: FormatterSchema, entity: any, maxWidthPerField: number): Promise<DrawResult[]> {
        let results: DrawResult[] = []
        let path = formatterSchema.path ?? formatterSchema.entityFieldPath
        if(!path) {
            if(formatterSchema.entityField) {
                const entityField = await EntityFieldDataStore.getInstance().fetchOne(formatterSchema.entityField).toPromise()
                path = `${entityField.path}`
            } else {
                return []
            }
        }
        let entityValue = _get(entity, `${path}.name`) || _get(entity, path)
                
        if(formatterSchema.linkedFormatter) {
            let subEntityId = entityValue
            if(entityValue && entityValue.ref) {
                subEntityId = entityValue.ref
            }
    
            let controllerString: string | undefined
            let entityType = formatterSchema.referenceType ?? formatterSchema.entityFieldType
            if(!entityType && entityValue?.referenceType) {
                entityType = entityValue.referenceType
            }
            if(entityType) {
                controllerString = getControllerStringFromEntityType(entityType)
            }
    
            if(subEntityId) {
                const subEntity = await fetchEntity(subEntityId, controllerString)
                const subFormResults: DrawResult[] = await this.drawFormatter(field, subEntity, maxWidthPerField, formatterSchema.linkedFormatter)
                results = results.concat(subFormResults)
            }
        } else {
            const drawValue = async (value: any) => {
                if(formatterSchema.formatters) {
                    for(const selectedFormatterSchema of formatterSchema.formatters) {
                        const subFormResults: DrawResult[] = await this.drawFormatterSchema(field, selectedFormatterSchema, value, maxWidthPerField)
                        results = results.concat(subFormResults)
                    }
                } else {
                    switch(formatterSchema.entityFieldType) {
                        case 'TranslatedString':
                            value = getTranslatedString(value, this.locale)
                            break
                        case 'String': 
                            if(value instanceof Object) {
                                if('value' in value) {
                                    value = value.value
                                }
                            }
                            break
                    }
                    results.push(this.drawText(value, 1, maxWidthPerField))
                }
            }
    
            switch(formatterSchema.entityFieldType) {
                case 'List': 
                    if(entityValue) {
                        for(const entry of entityValue) {
                            let entryWithFullPath = {}
                            _set(entryWithFullPath, path, entry)
                            await drawValue(entryWithFullPath)
                        }
                    }
                    break
                default:
                    await drawValue(entityValue)
                    break
            }
            
        }
    
        return results
    }

    private resizeFormatter(txtDrawResult: DrawResult, font: Font, fontMultiplier: number, oldFont?: Font, oldFontMultiplier?: number) {
        return this.resizeText(txtDrawResult, font, fontMultiplier, oldFont, oldFontMultiplier)
    }
}

function getControllerStringFromEntityType(entityType: string): string {
    const exceptions = ['ThesaurusTerm', 'Alias']
    if(exceptions.includes(entityType)) {
        switch(entityType) {
            case 'ThesaurusTerm': 
                return 'thesaurusTerm'
            case 'Alias':
                return 'aliases'
        }
    }
    
    const camelCaseToKebabCase = (str) => str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? "-" : "") + $.toLowerCase())
    let res: string = camelCaseToKebabCase(entityType)
    const lastChar = res.slice(-1)
    if(lastChar !== 's') {
        if(lastChar === 'y') {
            res = res.slice(0, -1)
            res += 'ies'
        }
        res += 's'
    }
    return res
}

function fetchEntity(id: string, controllerString?: string) {
    const uri = controllerString ? `/${controllerString}/${id}` : `/entity/${id}`
    const networkFirst = false

    return PROXY.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
        last(),
        catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
    ).toPromise()
}